import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

// Import email components (lazy-loaded for performance)
const EmailsList = React.lazy(() => import("./EmailsList"));
const NewEmail = React.lazy(() => import("./new"));
const EmailView = React.lazy(() => import("./EmailView"));
const InboundEmailDetail = React.lazy(() => import("./InboundEmailDetail"));
const AddressBook = React.lazy(() => import("./AddressBook").then(module => ({ default: module.AddressBook })));
const EmailAccountsAdmin = React.lazy(() => import("./EmailAccountsAdmin").then(module => ({ default: module.EmailAccountsAdmin })));
const ImprovMXAdmin = React.lazy(() => import("./ImprovMXAdmin").then(module => ({ default: module.ImprovMXAdmin })));
const Microsoft365Admin = React.lazy(() => import("./Microsoft365Admin").then(module => ({ default: module.Microsoft365Admin })));

const emailsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "emails",
    name: "Email Marketing",
    description: "Compose and send branded HTML emails",
    version: "1.0.1", // Changed version to force refresh
    enabled: true,
    category: "communication",
    order: 10,
    icon: "✉️",
    color: getColor("primaryBlue"),

    // ROUTES
    routes: [
        {
            path: "/emails",
            component: EmailsList,
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER"]
        },
        {
            path: "/emails/new",
            component: NewEmail,
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER"]
        },
        {
            path: "/emails/address-book",
            component: AddressBook,
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "ADDRESS_BOOK_ADMIN", "ADDRESS_BOOK_USER", "EMAIL_USER", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/emails/admin/accounts",
            component: EmailAccountsAdmin,
            permissions: ["EMAIL_INBOX_ADMIN", "EMAIL_ADMIN", "ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/emails/admin/improvmx",
            component: ImprovMXAdmin,
            permissions: ["EMAIL_INBOX_ADMIN", "EMAIL_ADMIN", "ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/emails/admin/microsoft365",
            component: Microsoft365Admin,
            permissions: ["EMAIL_INBOX_ADMIN", "EMAIL_ADMIN", "ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/email/:id",
            component: EmailView,
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER"]
        },
        {
            path: "/inbox/:id",
            component: InboundEmailDetail,
            permissions: ["EMAIL_ADMIN", "EMAIL_USER", "ADMIN", "MANAGER"]
        }
    ],

    // NAVIGATION
    navigation: [
        {
            label: "All Emails",
            path: "/emails",
            icon: "📧",
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER"]
        },
        {
            label: "Address Book",
            path: "/emails/address-book",
            icon: "📖",
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "ADDRESS_BOOK_ADMIN", "ADDRESS_BOOK_USER", "EMAIL_USER", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Compose Email",
            path: "/emails/new",
            icon: "✍️",
            permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER"]
        },
        {
            label: "Email Accounts Admin",
            path: "/emails/admin/accounts",
            icon: "🔧",
            permissions: ["TENANT_MASTER_ADMIN", "ADMIN", "EMAIL_ADMIN", "EMAIL_INBOX_ADMIN", "TENANT_ADMIN"]
        },
        {
            label: "ImprovMX Aliases",
            path: "/emails/admin/improvmx",
            icon: "📨",
            permissions: ["TENANT_MASTER_ADMIN", "ADMIN", "EMAIL_ADMIN", "EMAIL_INBOX_ADMIN"]
        },
        {
            label: "Microsoft 365",
            path: "/emails/admin/microsoft365",
            icon: "📧",
            permissions: ["TENANT_MASTER_ADMIN", "ADMIN", "EMAIL_ADMIN", "EMAIL_INBOX_ADMIN"]
        }
    ],

    // PERMISSIONS
    permissions: {
        view: ["EMAIL_ADMIN", "EMAIL_USER", "ADMIN", "MANAGER"],
        create: ["EMAIL_ADMIN", "EMAIL_USER", "ADMIN", "MANAGER"],
        edit: ["EMAIL_ADMIN", "ADMIN", "MANAGER"],
        delete: ["EMAIL_ADMIN", "ADMIN"],
        send: ["EMAIL_ADMIN", "EMAIL_USER", "ADMIN", "MANAGER"]
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Compose Email",
            path: "/emails/new",
            icon: "✍️",
            description: "Create a new email"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.1",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready email marketing system",
                "✅ Complete inbox and outbox management",
                "✅ Advanced address book with contact management",
                "✅ Multi-account email administration",
                "✅ Communication task tracking",
                "✅ Attachment support for all email types",
                "✅ Project integration for email-to-task conversion"
            ],
            notes: "Official stable release with comprehensive email communication features."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-09-01",
            title: "Refactoring & Tagging",
            features: [
                "🔨 Removed deprecated EmailInbox component",
                "✨ Enhanced EmailsList with tagging features (450 insertions)",
                "✨ Improved InboundEmailDetail with tags (258 insertions)",
                "✨ Better email organization and filtering",
                "🔨 Code cleanup and optimization"
            ],
            notes: "Major refactoring improving email organization with tagging system."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-08-31",
            title: "UI Enhancements & Polish",
            features: [
                "✨ Enhanced AddressBook UI (116 lines)",
                "✨ Improved EmailsList interface (103 lines)",
                "✨ Better InboundEmailDetail presentation (71 lines)",
                "✨ Refined email composition UI (60 lines)",
                "✨ Improved responsive design"
            ],
            notes: "UI refinements for better user experience across all email components."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-08-30",
            title: "Reply Functionality & Email Composer",
            features: [
                "✨ Added reply functionality to InboundEmailDetail (33 lines)",
                "✨ Enhanced NewEmail with reply support (44 lines)",
                "✨ Created email composer component",
                "✨ Improved email threading",
                "🔨 Refactored EmailView and NewEmail (438 deletions)"
            ],
            notes: "Reply and threading capabilities for better email communication."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-29",
            title: "Project Integration & Performance",
            features: [
                "✨ Added ConvertToProject functionality (727 lines)",
                "✨ Enhanced EmailView with project integration (32 lines)",
                "✨ Improved InboundEmailDetail workflow (59 lines)",
                "✨ Refactored EmailsList for better query performance (78 lines)",
                "✨ Added client email integration (154 lines)"
            ],
            notes: "Deep integration with project management and performance optimizations."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-28",
            title: "UI Responsiveness Update",
            features: [
                "✨ Enhanced AddressBook responsiveness (81 lines)",
                "✨ Updated EmailAccountsAdmin UI (228 lines)",
                "✨ Improved EmailInbox consistency (272 lines)",
                "✨ Better mobile experience across all components"
            ],
            notes: "Comprehensive responsiveness improvements for mobile and tablet devices."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-26",
            title: "Address Book & Account Management",
            features: [
                "✨ Enhanced EmailAccountsAdmin (177 lines)",
                "✨ Advanced NewEmail features (394 insertions)",
                "✨ Improved EmailView with attachments (285 insertions)",
                "✨ Address book functionality (326 lines)",
                "✨ Better email account configuration"
            ],
            notes: "Address book and multi-account management features."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-08-25",
            title: "Feature Expansion - Inbound & Attachments",
            features: [
                "✨ Added AddressBook component (670 lines)",
                "✨ Created EmailInbox component (544 lines)",
                "✨ Implemented EmailAccountsAdmin (508 lines)",
                "✨ Enhanced with attachment support (227 insertions)",
                "✨ Added search and filter functionality (1,405 insertions)",
                "✨ Created UnifiedEmailCard component (165 lines)",
                "✨ Added CommunicationTaskList (417 lines)",
                "✨ Implemented CommunicationTaskModal (458 lines)",
                "✨ Created InboundEmailDetail component (368 lines)",
                "✨ Enhanced brand configuration integration"
            ],
            notes: "Massive feature expansion with inbound email, attachments, and task management."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-24",
            title: "Initial Email Management System",
            features: [
                "✅ Created EmailView component (473 lines)",
                "✅ Created EmailsList component (397 lines)",
                "✅ Created NewEmail component (388 lines)",
                "✅ Basic email composition and sending",
                "✅ Markdown editor integration (remark-gfm)",
                "✅ Initial GraphQL integration",
                "✅ Draft and sent email management",
                "✅ Module configuration and routing"
            ],
            notes: "Initial implementation of the email marketing system with 1,334 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []
};

// Debug logging
console.log('📧 Email Module Config loaded with navigation:', emailsModuleConfig.navigation);

export default emailsModuleConfig;